import type { ApolloError } from '@apollo/client';
import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import type { PageFunctionType } from '@shared/core/analytics/telemetry/types';
import type { DesignLayoutType, EventPageVisibility, LeadStateV2 } from '@graphql/generated';
import type { GuestSiteQueryParams } from './packages/layout-engine/common/utils/useGuestSiteQueryParams';
import type { PagePrefix } from '@shared/core/analytics/context';

export type PagesForTelemetry =
  | 'welcome'
  | 'faq'
  | 'story'
  | 'travel'
  | 'tidbits'
  | 'registrylinks'
  | 'schedule'
  | 'moments'
  | 'weddingparty'
  | 'rsvp'
  | 'accommodations'
  | 'custom'
  | 'headerLayoutUpgradeDialog';

export type SecurityInfo = {
  requiresPassword: boolean;
  isUnlocked?: boolean;
};

export type EnabledPagesInfo = {
  slug: string;
  title: string;
  visibility: EventPageVisibility;
};

export type RoomBlocksInfo = {
  leadState?: LeadStateV2;
  availableCount: number;
  daysUntilSoonestCutOff?: number;
};

export type GuestSiteLoadTelemetry = {
  layout: DesignLayoutType;
  userRole: string;
  security: SecurityInfo;
  enabledPages: EnabledPagesInfo[];
  daysUntilEvent?: number;
} & GuestSiteQueryParams;

const createPageTelemetryData = (defaultPageName: string, action: string) => (userRole: string, args: GuestSiteQueryParams, duration?: string) => ({
  category: 'wedding',
  pagePrefix: 'guest' as PagePrefix,
  page: duration ? `${duration}.${defaultPageName}` : defaultPageName,
  action,
  extraPageInfo: {
    role: userRole,
    ...args,
  },
});

const pages: { [tab in PagesForTelemetry]: PageFunctionType } = {
  welcome: createPageTelemetryData('Welcome', 'ViewWelcome'),
  faq: createPageTelemetryData('faq', 'ViewFAQ'),
  story: createPageTelemetryData('story', 'ViewStory'),
  tidbits: createPageTelemetryData('tidbits', 'ViewTidbits'),
  travel: createPageTelemetryData('travel', 'ViewTravel'),
  registrylinks: createPageTelemetryData('registrylinks', 'ViewRegistryLinks'),
  schedule: createPageTelemetryData('schedule', 'ViewSchedule'),
  moments: createPageTelemetryData('moments', 'ViewMoments'),
  weddingparty: createPageTelemetryData('weddingParty', 'ViewWeddingParty'),
  rsvp: createPageTelemetryData('RSVP', 'ViewRSVP'),
  accommodations: createPageTelemetryData('accommodations', 'ViewAccommodations'),
  custom: createPageTelemetryData('custom', 'ViewCustom'),
  headerLayoutUpgradeDialog: createPageTelemetryData('HeaderLayoutUpgradeDialog', 'ViewHeaderLayoutUpgradeDialog'),
};

const guestSiteTelemetry = createTelemetryObject({
  actions: {
    bookYourStayPageViewed: (args: GuestSiteQueryParams) => ({
      category: 'wedding',
      pagePrefix: 'guest',
      page: 'book-your-stay',
      action: 'TravelButtonInteracted',
      extraInfo: { ...args, name: 'viewBookYourStay' },
    }),

    trackError: (mutation: string, error: ApolloError | Error | unknown, extra = {}) => ({
      category: 'wedding',
      action: 'Mutation',
      actionType: 'error',
      extraInfo: { error, mutation, ...extra },
    }),

    adminPreviewClick: () => ({
      category: 'admin',
      action: 'OpenWebsite',
      actionType: 'click',
      label: 'CharmPreview',
    }),

    announcementBannerChange: (title: string, status: 'Shown' | 'Dismissed') => ({
      action: 'AnnouncementBannerStatus',
      category: 'wedding',
      label: title,
      extraInfo: {
        status: status,
      },
    }),

    notPublishedBanner: (label: 'Shown' | 'Dismissed' | 'Published') => ({
      action: 'NotPublishedWarningStatus',
      category: 'wedding',
      label,
    }),

    // MENU
    navMenuOpenedChange: (label: 'opened' | 'closed') => ({
      action: 'NavMenuOpened',
      category: 'wedding',
      label,
    }),

    navMenuItemClicked: (pageClicked: string) => ({
      action: 'ButtonInteracted',
      category: 'wedding',
      extraInfo: {
        name: 'GuestSiteNavClicked',
        pageClicked,
      },
    }),

    // Photo Editor
    photoEditorOpened: (label: 'opened' | 'closed') => ({
      action: 'PagePhotoEditorStatus',
      category: 'wedding',
      label,
    }),

    photoEditorChangePhotoClicked: () => ({
      action: 'PagePhotoEditorChangePhoto',
      actionType: 'click',
      category: 'wedding',
    }),

    photoEditorUpdateFocus: (previewsFocus: unknown, currentFocus: unknown) => ({
      action: 'PagePhotoEditorFocusChanged',
      category: 'wedding',
      extraInfo: {
        previewsFocus,
        currentFocus,
      },
    }),

    // Welcome Section Aloha
    welcomeViewDetailsButtonClicked: () => ({
      action: 'WelcomeViewDetailsButtonClicked',
      actionType: 'click',
      category: 'wedding',
    }),

    welcomeRSVPButtonClicked: () => ({
      action: 'WelcomeRSVPButtonClicked',
      actionType: 'click',
      category: 'wedding',
    }),

    welcomeNotYouButtonClicked: () => ({
      action: 'WelcomeRSVPButtonClicked',
      actionType: 'click',
      category: 'wedding',
    }),

    // APP Section
    weHaveAnAppClicked: (app: string) => ({
      action: 'WeHaveAnAppClicked',
      actionType: 'click',
      category: 'wedding',
      label: app,
    }),

    editEventClicked: () => ({
      action: 'EditEventClicked',
      actionType: 'click',
      category: 'wedding',
    }),

    // Footer
    footerItemClicked: (item: string) => ({
      action: 'FooterItemClicked',
      actionType: 'click',
      category: 'wedding',
      label: item,
    }),

    websiteVisibilityPublish: (eventId: string) => ({
      category: 'adminWysiwyg',
      action: 'PublishGoLive',
      extraInfo: {
        eventId,
      },
    }),
    headerLayoutUpgradeDialogUpgradeClicked: () => ({
      category: 'wedding',
      action: 'ButtonInteracted',
      page: 'HeaderLayoutUpgradeDialog',
      extraInfo: { name: 'headerLayoutUpgradeDialog_upgrade' },
    }),
    headerLayoutUpgradeDialogNoThanksClicked: () => ({
      category: 'wedding',
      action: 'ButtonInteracted',
      page: 'HeaderLayoutUpgradeDialog',
      extraInfo: { name: 'headerLayoutUpgradeDialog_noThanks' },
    }),
    headerLayoutUpgradeDialogAllDoneClicked: () => ({
      category: 'wedding',
      action: 'ButtonInteracted',
      page: 'HeaderLayoutUpgradeDialog',
      extraInfo: { name: 'headerLayoutUpgradeDialog_allDone' },
    }),
  },
  pages,
});

const { TelemetryProvider, useTelemetry: useGuestSiteTelemetry, enrichTelemetryExtraInfo } = createTelemetry(guestSiteTelemetry);
export { TelemetryProvider, useGuestSiteTelemetry, enrichTelemetryExtraInfo };
